import type { PermissionAccessSnapshot } from './permissionAccess'

import {
  getAccountDetailPackageId,
  isFreePlanPackage,
  useAccount,
  useAccountSession,
  useFeatureFlags,
  useRbac,
  useWorkspace,
  type SessionStatus,
} from '@/features/account'
import { useIntegrations } from '@/features/integrations'
import { useOrpheusRbac } from '@/features/orpheus'
import { useReportAccess } from '@/features/reports'
import { useWorkflowRbac } from '@/features/workflow'

export interface PermissionSnapshot extends PermissionAccessSnapshot {
  status: SessionStatus
}

/** One live permission reader shared by the initial guard and post-bootstrap revalidation. */
export function readPermissionSnapshot(): PermissionSnapshot {
  const session = useAccountSession()
  const account = useAccount()
  const rbac = useRbac()
  const workspace = useWorkspace()
  const flags = useFeatureFlags()
  const orpheus = useOrpheusRbac()
  const workflow = useWorkflowRbac()
  const integrations = useIntegrations()
  const reports = useReportAccess()
  return {
    status: session.status.value,
    isSysAdmin: rbac.isSysAdmin.value,
    // ORP-21835 item 4: the admin-bypass persona is exempt from the MCP branch exactly as a
    // sysadmin is. Read through `useRbac` (ledger A4 — every authorization field comes from 07's
    // seam, never from a store or auth), which already compares the optional wire bit `=== true`,
    // so an absent one arrives here as `false` and grants nothing.
    canAccessAdminPanel: rbac.canAccessAdminPanel.value,
    isAccountHolder: rbac.isAccountHolder.value,
    workspaceCount: workspace.workspaces.value.length,
    onlyDashboard: rbac.onlyDashboard.value,
    // ORP-21656 AC 2: the ONE machine-readable field the MCP branch reads. `=== true` is the
    // fail-safe direction — a payload without the field (an older backend, a degraded boot) is
    // treated as a web account, so nothing is locked out of the product it did buy.
    isMcpOnly: account.accountDetail.value?.is_mcp_only === true,
    isFreePlan: isFreePlanPackage(getAccountDetailPackageId(account.accountDetail.value)),
    hasPermission: (key) => {
      if (key.startsWith('flag:')) return rbac.isSysAdmin.value || flags.checkFlag(key.slice(5))
      if (key.startsWith('integration:')) return integrations.hasIntegration(key.slice(12))
      if (key.startsWith('report:')) {
        const reportId = Number(key.slice(7))
        return Number.isInteger(reportId) && reports.checkReport(reportId)
      }
      if (key === 'orpheus') return orpheus.canUseOrpheus.value
      if (key === 'workflow') return workflow.canAccess.value
      return rbac.hasPermission(key)
    },
  }
}
